import { computed, ref, watch } from 'vue';

import { defineStore } from 'pinia';

import {
  fetchEbayConversation,
  fetchEbayConversations,
  fetchEbayMessagesSummary,
  fetchEbayReplies,
  fetchEbayReply,
  markEbayConversationRead,
  postEbayReply,
  refreshEbayConversation,
} from '@/api/ebayMessages.js';
import { messageQueryIssue } from '@/components/ebay/messages/ebayMessagesQuery.js';
import {
  newIdempotencyKey,
  readReplyDraft,
  removeReplyDraft,
  replyLength,
  writeReplyDraft,
} from '@/helpers/ebayReplyDrafts.js';
import { useEbayAccountStore } from '@/stores/ebayAccount.js';

// The eBay buyer-messages inbox (docs/ebay/MESSAGES_PLAN.md, unit D1 of §4) and its write
// side (unit D2): the reply composer's drafts and send flow, mark read/unread, and "load full
// conversation". The write side talks to the ETL's reply routes, never to olap.
//
// Scoped to the SELECTED eBay account, not the business profile: switching accounts is a
// different mailbox, so every filter, the loaded conversations, the open thread and the
// summary are dropped rather than left stale under the new account's header.
export const FILTERS = ['unanswered', 'unread', 'all', 'archived'];
export const DEFAULT_FILTER = 'unanswered';
const PAGE_SIZE = 30;

// eBay's limit on one message (error 355013), checked by the ETL as well.
export const MAX_REPLY_LENGTH = 2000;

// Replies are polled every second for the first 30 s, then every 5 s (plan §4), then every 30 s
// once it has been open for five minutes. Polling never gives up while the page is open and the
// ETL's status is non-terminal: the ETL can keep a reply open far longer than any local timer
// (PENDING up to 30 min, RECONCILING ~12.5 min, or queued behind another reply to the same
// conversation), and a duplicate send to a real buyer is the worst outcome. So a reply is
// 'unknown' ONLY when GET /replies/{id} itself says UNKNOWN — never because of a local timer or a
// count of failed reads. POLL_UNREADABLE_AFTER failed reads in a row only flag the reply as
// "can't check its status right now", which is still in flight (no send again, no discard).
const POLL_FAST_MS = 1000;
const POLL_FAST_WINDOW_MS = 30_000;
const POLL_SLOW_MS = 5000;
const POLL_BACKOFF_AFTER_MS = 5 * 60_000;
const POLL_BACKOFF_MS = 30_000;
const POLL_UNREADABLE_AFTER = 5;
const REFETCH_AFTER_SENT_MS = 2000;
// How far before the browser's own POST time the ETL's createdAt may lie when a reply whose
// POST got no answer is matched back (loadReplies): the two clocks are not the same clock.
const ADOPT_CLOCK_SKEW_MS = 2 * 60_000;
const OPEN_STATUSES = ['PENDING', 'SENDING', 'RECONCILING'];
const SETTLED_STATUSES = ['SUCCEEDED', 'FAILED', 'UNKNOWN'];
const REPLIES_OFF_CODES = ['MESSAGE_REPLIES_DISABLED', 'MESSAGES_UNAVAILABLE'];

// The ETL nests its stable code under FastAPI's `detail`: `{ detail: { errorCode, error } }`.
const errorCodeOf = (error) => {
  const code = error?.response?.data?.detail?.errorCode;
  return typeof code === 'string' && code ? code : null;
};

// The nav badge's count, mirrored from `summary` (see the watch below). A plain module-level
// ref, not read off the store instance: the nav config's getter runs during VerticalNavLink's
// render, with no guarantee that a full `useAccountStore()` → `useRoute()`/`useRouter()`
// chain can resolve at that point (verified: calling `useEbayMessagesStore()` — which
// transitively creates `useEbayAccountStore()` → `useAccountStore()` — outside a mounted
// component throws "inject() can only be used inside setup()"). A ref needs no injection
// context to read, so the nav can import this directly and never touch the store chain.
export const unansweredMessagesCount = ref(null);

export const useEbayMessagesStore = defineStore('ebayMessages', () => {
  const filter = ref(DEFAULT_FILTER);
  const q = ref('');
  const sort = ref('newest');
  const marketplaceId = ref('');
  const conversationType = ref('');
  const queryIssue = computed(() => messageQueryIssue(q.value));
  const listSettled = ref(false);
  const listing = ref(null); // listingId filter, arrived at via a deep link
  const order = ref(null); // orderId filter, arrived at via a deep link

  const conversations = ref([]);
  const nextCursor = ref(null);
  const counts = ref({ unanswered: 0, unread: 0 });
  const loading = ref(false);
  const loadingMore = ref(false);
  const error = ref(null);

  const selectedId = ref(null);
  const selectionScope = computed(() => {
    if (!selectedId.value || !listSettled.value || loading.value || loadingMore.value || error.value || queryIssue.value) return null;
    if (conversations.value.some((row) => row.conversationId === selectedId.value)) return null;
    return nextCursor.value ? 'not-loaded' : 'outside';
  });
  const threads = ref({}); // conversationId -> { conversation, messages, threadComplete }
  // Per conversation id, not a single flag: a stale response from a fetch for a conversation
  // that is no longer selected must not flip the CURRENTLY selected thread's loading/error
  // state (or vice versa) just because it happened to land around the same time.
  const threadLoading = ref({}); // conversationId -> boolean
  const threadErrors = ref({}); // conversationId -> Error
  const olderLoading = ref({});
  const olderErrors = ref({});
  let olderTokens = {};

  const summary = ref(null); // { unanswered, unread, oldestUnansweredAt }
  const summaryError = ref(null);

  // The reply composer. All of it is per conversation id, and all of it lives here rather than
  // in the composer component so a send survives switching to another conversation, and a
  // draft / idempotency key has exactly one owner.
  //
  // drafts:           conversationId -> { text, key }; written through to localStorage. `key` is
  //                   the idempotency key the text was last sent under (null until it is sent).
  // pendingReplies:   conversationId -> { status, text, mutationId, errorCode, reuseKey }, the
  //                   one reply in flight or unresolved. status is 'sending' (POST in flight),
  //                   'queued' (accepted, being polled; `unreadable: true` while its status can't
  //                   be read right now — still in flight), 'failed' or 'unknown' (the ETL itself
  //                   said UNKNOWN). A reply that was sent is not kept here — it becomes an
  //                   optimistic bubble.
  // optimisticReplies: conversationId -> [{ mutationId, messageId, text, createdDate }], sent
  //                   replies shown before the thread refetch has caught up with them.
  const drafts = ref({});
  const pendingReplies = ref({});
  const optimisticReplies = ref({});
  // Learned from a 503 MESSAGE_REPLIES_DISABLED (there is no capabilities route): the ETL
  // switches every message route off with one flag, so one answer covers all conversations.
  const repliesDisabled = ref(false);
  const actionBusy = ref({}); // conversationId -> 'read' | 'refresh'

  // Bumped by every `reset()`. The page watches this — rather than the eBay account
  // selection directly — so it re-applies the URL's filter/listing/order/q/c strictly
  // AFTER this store has cleared the previous account's state, whichever store's watcher
  // happened to register first (Pinia setup stores run their own watches once, at first
  // use, which could be from the nav badge rendering before the page ever mounts).
  const resetToken = ref(0);
  // Whether the reset that just bumped resetToken was a genuine account-to-account switch
  // (true) or the first selection this store instance has ever seen (false, previousId was
  // null — a fresh load or a deep link, where replaying the URL's c/listing/order is right).
  // The page reads this to decide whether to replay the URL or strip it instead.
  const resetWasSwitch = ref(false);

  // Guards against a stale response landing after a newer filter/search/account change
  // fired a second request — the same pattern as stores/channelAccount.js's `inflight`.
  // Every guard here doubles as the fix for "don't let the old account's in-flight request
  // land under the new account": `reset()` replaces these objects wholesale, so a response
  // that resolves after the switch always finds its token gone and is dropped, however far
  // along the request already was (the request itself still carried the OLD account's
  // Ebayaccountid header — that's correct, it's what was selected when it was sent — this
  // only stops its ANSWER from being written into the new account's state).
  let listToken = null;
  let summaryToken = null;
  let threadTokens = {};
  // Reply polling, one loop per conversation: the token says which loop is current, `pauses`
  // holds the sleeping loop's timer so a reset can wake and stop it. `refetchTimers` are the
  // pending "refetch the thread after a send" timers.
  let replyPolls = {};
  let pauses = {};
  const refetchTimers = new Set();
  // Conversations whose 'unknown' reply is being read back before a confirmed resend.
  const rechecking = new Set();
  // Bumped by `stopReplyPolling()` (the page going away). A POST or a /replies read that was
  // already in the air when that happened must not start a poll nobody is watching.
  let idleEpoch = 0;
  // Sticky: once this store instance has ever had a real account selected, it stays true —
  // even across an intermediate null (a business-profile switch nulls the selection before
  // the new profile's accounts load). Seeded from whatever is ALREADY selected at store
  // creation, not just flipped inside the watcher below: a business profile with its eBay
  // account already selected when this store is first created (e.g. from a prior eBay-page
  // visit) must not read as "no account yet" just because the watcher hasn't fired.
  let hadAccount = useEbayAccountStore().selectedAccountId != null;

  const fetchConversations = async ({ reset = false } = {}) => {
    if (reset) {
      conversations.value = [];
      nextCursor.value = null;
    } else if (!nextCursor.value) {
      return; // nothing more to load
    }

    const token = {};
    listToken = token;
    if (reset) listSettled.value = false;
    if (queryIssue.value || Array.from(marketplaceId.value).length > 32) {
      loading.value = false;
      loadingMore.value = false;
      error.value = null;
      return;
    }
    loading.value = reset;
    loadingMore.value = !reset;
    error.value = null;
    try {
      const data = await fetchEbayConversations({
        filter: filter.value,
        listingId: listing.value,
        orderId: order.value,
        q: q.value,
        sort: sort.value,
        marketplaceId: marketplaceId.value || null,
        conversationType: conversationType.value || null,
        cursor: reset ? null : nextCursor.value,
        limit: PAGE_SIZE,
      });
      if (listToken !== token) return; // superseded by a newer request
      conversations.value = reset ? (data.conversations || []) : [...conversations.value, ...(data.conversations || [])];
      nextCursor.value = data.nextCursor ?? null;
      if (data.counts) counts.value = data.counts;
      listSettled.value = true;
    } catch (e) {
      if (listToken !== token) return;
      error.value = e;
    } finally {
      if (listToken === token) {
        loading.value = false;
        loadingMore.value = false;
      }
    }
  };

  const fetchMore = () => {
    if (!nextCursor.value || loading.value || loadingMore.value) return Promise.resolve();
    return fetchConversations({ reset: false });
  };

  // Sets any subset of the list params and reloads from the first page. Partial on
  // purpose: a filter-chip click passes only `{ filter }`, the page's route sync passes
  // all four at once — both replace only what they name.
  const setParams = (partial = {}) => {
    if ('filter' in partial) filter.value = FILTERS.includes(partial.filter) ? partial.filter : DEFAULT_FILTER;
    if ('listing' in partial) listing.value = partial.listing || null;
    if ('order' in partial) order.value = partial.order || null;
    if ('q' in partial) q.value = partial.q || '';
    if ('sort' in partial) sort.value = partial.sort === 'oldest_unanswered' ? partial.sort : 'newest';
    if (filter.value !== 'unanswered') sort.value = 'newest';
    if ('marketplaceId' in partial) marketplaceId.value = String(partial.marketplaceId || '').trim();
    if ('conversationType' in partial) conversationType.value = ['FROM_MEMBERS', 'FROM_EBAY'].includes(partial.conversationType) ? partial.conversationType : '';
    return fetchConversations({ reset: true });
  };

  // A sent reply is shown as an optimistic bubble until the thread itself holds it: drop the
  // ones whose message id is now among the loaded messages.
  const pruneOptimistic = (conversationId, loadedMessages) => {
    const shown = optimisticReplies.value[conversationId];
    if (!shown?.length) return;
    const loaded = new Set((loadedMessages || []).map((m) => m.messageId));
    const remaining = shown.filter((reply) => !loaded.has(reply.messageId));
    if (remaining.length === shown.length) return;
    optimisticReplies.value = { ...optimisticReplies.value, [conversationId]: remaining };
  };

  // Cached in `threads`, refetched only when the loaded list shows the conversation has
  // moved on (a newer message, or the count changed) since it was cached — not merely to
  // save a request: this GET reads ClickHouse through olap only. It never calls eBay and so
  // can never mark anything read; that risk belongs solely to D2's "Load full conversation"
  // (a live eBay getConversation call), not to this cache policy.
  const fetchThread = async (conversationId, { force = false } = {}) => {
    if (!conversationId) return undefined;
    const cached = threads.value[conversationId];
    const row = conversations.value.find((c) => c.conversationId === conversationId);
    // Strictly NEWER than the cache, not merely different: the list row is a coarser view
    // that can legitimately lag one step behind the cached thread (e.g. right after `force`
    // refetched the thread, the list's own next page hasn't caught up yet). Only a row that
    // has moved AHEAD of the cache is evidence the thread itself is now stale.
    const stale = !!(cached && row && (
      String(row.latestMessageAt || '') > String(cached.conversation?.latestMessageAt || '')
      || (row.messageCount ?? 0) > (cached.conversation?.messageCount ?? 0)
    ));
    if (!force && cached && !stale) return cached;

    const token = {};
    threadTokens[conversationId] = token;
    // An in-flight older page must not merge into a fresh first page.
    delete olderTokens[conversationId];
    olderLoading.value = { ...olderLoading.value, [conversationId]: false };
    olderErrors.value = { ...olderErrors.value, [conversationId]: null };
    threadLoading.value = { ...threadLoading.value, [conversationId]: true };
    threadErrors.value = { ...threadErrors.value, [conversationId]: null };
    try {
      const data = await fetchEbayConversation(conversationId);
      if (threadTokens[conversationId] !== token) return undefined; // superseded, or the account was reset
      threads.value = { ...threads.value, [conversationId]: data };
      pruneOptimistic(conversationId, data.messages);
      return data;
    } catch (e) {
      if (threadTokens[conversationId] !== token) return undefined;
      threadErrors.value = { ...threadErrors.value, [conversationId]: e };
      return undefined;
    } finally {
      if (threadTokens[conversationId] === token) {
        threadLoading.value = { ...threadLoading.value, [conversationId]: false };
      }
    }
  };

  const fetchOlderMessages = async (conversationId) => {
    const cached = threads.value[conversationId];
    if (!cached?.nextCursor || olderLoading.value[conversationId] || threadLoading.value[conversationId]) return;
    const token = {};
    olderTokens[conversationId] = token;
    olderLoading.value = { ...olderLoading.value, [conversationId]: true };
    olderErrors.value = { ...olderErrors.value, [conversationId]: null };
    try {
      const data = await fetchEbayConversation(conversationId, { cursor: cached.nextCursor });
      if (olderTokens[conversationId] !== token) return;
      const current = threads.value[conversationId];
      if (!current || current.nextCursor !== cached.nextCursor) return;
      const held = new Set(current.messages.map((message) => message.messageId));
      const older = (data.messages || []).filter((message) => !held.has(message.messageId));
      threads.value = {
        ...threads.value,
        [conversationId]: { ...current, messages: [...older, ...current.messages], nextCursor: data.nextCursor ?? null, truncated: !!data.nextCursor },
      };
    } catch (e) {
      if (olderTokens[conversationId] === token) olderErrors.value = { ...olderErrors.value, [conversationId]: e };
    } finally {
      if (olderTokens[conversationId] === token) olderLoading.value = { ...olderLoading.value, [conversationId]: false };
    }
  };

  const selectConversation = (conversationId) => {
    selectedId.value = conversationId || null;
    if (selectedId.value) fetchThread(selectedId.value);
  };

  const fetchSummary = async () => {
    const token = {};
    summaryToken = token;
    summaryError.value = null;
    try {
      const data = await fetchEbayMessagesSummary();
      if (summaryToken !== token) return; // superseded by a newer request (or an account switch)
      summary.value = data;
    } catch (e) {
      if (summaryToken !== token) return;
      summaryError.value = e;
    }
  };

  const selectedAccountId = () => useEbayAccountStore().selectedAccountId;

  const noteRepliesOff = (code) => {
    if (REPLIES_OFF_CODES.includes(code)) repliesDisabled.value = true;
  };

  // After a write changed a conversation: merge the new fields into its loaded list row and
  // move the two filter counts by the same delta, so the chips don't wait for a reload.
  const patchRow = (conversationId, fields) => {
    const index = conversations.value.findIndex((c) => c.conversationId === conversationId);
    if (index < 0) return;
    const before = conversations.value[index];
    const after = { ...before, ...fields, conversationId };
    const next = [...conversations.value];
    next[index] = after;
    conversations.value = next;
    const move = (key, was, now) => {
      if (was === now) return;
      counts.value = { ...counts.value, [key]: Math.max(0, (counts.value[key] ?? 0) + (now ? 1 : -1)) };
    };
    move('unanswered', !!before.awaitingReply, !!after.awaitingReply);
    move('unread', (before.unreadCount ?? 0) > 0, (after.unreadCount ?? 0) > 0);
  };

  const setUnreadCount = (conversationId, unreadCount) => {
    const cached = threads.value[conversationId];
    if (cached?.conversation) {
      threads.value = {
        ...threads.value,
        [conversationId]: { ...cached, conversation: { ...cached.conversation, unreadCount } },
      };
    }
    patchRow(conversationId, { unreadCount });
  };

  const setBusy = (conversationId, action) => {
    const next = { ...actionBusy.value };
    if (action) next[conversationId] = action; else delete next[conversationId];
    actionBusy.value = next;
  };

  // Mark read / unread: a plain synchronous ETL call (never a queued mutation, never triggered
  // by a reply). Resolves `{ ok: true }` or `{ ok: false, errorCode }`; it does not throw.
  const markRead = async (conversationId, read = true) => {
    const accountId = selectedAccountId();
    if (accountId == null || !conversationId) return { ok: false, errorCode: 'NO_ACCOUNT' };
    if (actionBusy.value[conversationId]) return { ok: false, errorCode: 'BUSY' };
    const epoch = resetToken.value;
    setBusy(conversationId, 'read');
    try {
      await markEbayConversationRead(conversationId, { ebayAccountId: accountId, read });
      if (epoch !== resetToken.value) return { ok: false, errorCode: 'SUPERSEDED' };
      // The ETL wrote the same change to ClickHouse before answering: unread is 1 or 0.
      setUnreadCount(conversationId, read ? 0 : 1);
      fetchSummary();
      return { ok: true };
    } catch (e) {
      if (epoch !== resetToken.value) return { ok: false, errorCode: 'SUPERSEDED' };
      const errorCode = errorCodeOf(e) ?? (e?.response ? 'REQUEST_FAILED' : 'NETWORK');
      noteRepliesOff(errorCode);
      return { ok: false, errorCode };
    } finally {
      if (epoch === resetToken.value) setBusy(conversationId, null);
    }
  };

  // "Load full conversation": the ETL reads the thread's older pages live from eBay (which may
  // mark it read there), then the thread is refetched from olap so what it stored shows up.
  const refreshThread = async (conversationId) => {
    const accountId = selectedAccountId();
    if (accountId == null || !conversationId) return { ok: false, errorCode: 'NO_ACCOUNT' };
    if (actionBusy.value[conversationId]) return { ok: false, errorCode: 'BUSY' };
    const epoch = resetToken.value;
    setBusy(conversationId, 'refresh');
    try {
      const result = await refreshEbayConversation(conversationId, { ebayAccountId: accountId });
      if (epoch !== resetToken.value) return { ok: false, errorCode: 'SUPERSEDED' };
      const thread = await fetchThread(conversationId, { force: true });
      if (thread?.conversation) patchRow(conversationId, thread.conversation);
      fetchSummary();
      return {
        ok: true,
        messagesAdded: result?.messagesAdded ?? 0,
        threadComplete: !!result?.threadComplete,
      };
    } catch (e) {
      if (epoch !== resetToken.value) return { ok: false, errorCode: 'SUPERSEDED' };
      const errorCode = errorCodeOf(e) ?? (e?.response ? 'REQUEST_FAILED' : 'NETWORK');
      noteRepliesOff(errorCode);
      return { ok: false, errorCode };
    } finally {
      if (epoch === resetToken.value) setBusy(conversationId, null);
    }
  };

  const setPending = (conversationId, record) => {
    pendingReplies.value = { ...pendingReplies.value, [conversationId]: record };
  };

  const dropPending = (conversationId) => {
    const next = { ...pendingReplies.value };
    delete next[conversationId];
    pendingReplies.value = next;
  };

  const saveDraft = (conversationId, draft, accountId = selectedAccountId()) => {
    drafts.value = { ...drafts.value, [conversationId]: draft };
    writeReplyDraft(accountId, conversationId, draft);
  };

  const clearDraft = (conversationId, accountId = selectedAccountId()) => {
    const next = { ...drafts.value };
    delete next[conversationId];
    drafts.value = next;
    removeReplyDraft(accountId, conversationId);
  };

  // Reads the stored draft into memory the first time a conversation's composer opens.
  const loadDraft = (conversationId) => {
    if (!conversationId || drafts.value[conversationId]) return;
    const stored = readReplyDraft(selectedAccountId(), conversationId);
    if (stored) drafts.value = { ...drafts.value, [conversationId]: stored };
  };

  // Typing. An edited text is a different reply, so it drops the idempotency key (the next send
  // mints a new one) and clears a stale failure notice about the old text. An unresolved
  // 'unknown' notice stays: whatever the seller types next, the earlier reply may have gone out.
  // A POST that may have been saved (`maybeSaved`) locks the text instead: only a retry under
  // the same key — which replays the saved reply or starts the one and only — can settle it, and
  // an edit would mint a new key and send a second message if the first was saved after all.
  const setDraftText = (conversationId, text) => {
    if (!conversationId) return;
    if (pendingReplies.value[conversationId]?.maybeSaved) return;
    if ((drafts.value[conversationId]?.text ?? '') === text) return;
    if (text) saveDraft(conversationId, { text, key: null });
    else clearDraft(conversationId);
    if (pendingReplies.value[conversationId]?.status === 'failed') dropPending(conversationId);
  };

  const pause = (conversationId, ms) => new Promise((resolve) => {
    const id = setTimeout(() => {
      delete pauses[conversationId];
      resolve();
    }, ms);
    pauses[conversationId] = { id, resolve };
  });

  // Stops a conversation's poll loop: its token is dropped and a sleeping loop is woken so it
  // sees that and exits instead of waiting out its timer.
  const cancelPoll = (conversationId) => {
    delete replyPolls[conversationId];
    const sleeping = pauses[conversationId];
    if (sleeping) {
      clearTimeout(sleeping.id);
      delete pauses[conversationId];
      sleeping.resolve();
    }
  };

  const addOptimistic = (conversationId, reply) => {
    const shown = optimisticReplies.value[conversationId] ?? [];
    if (shown.some((r) => r.mutationId === reply.mutationId)) return;
    optimisticReplies.value = { ...optimisticReplies.value, [conversationId]: [...shown, reply] };
  };

  const scheduleThreadRefetch = (conversationId) => {
    const timer = setTimeout(async () => {
      refetchTimers.delete(timer);
      const thread = await fetchThread(conversationId, { force: true });
      if (thread?.conversation) patchRow(conversationId, thread.conversation);
      fetchSummary();
    }, REFETCH_AFTER_SENT_MS);
    refetchTimers.add(timer);
  };

  // The ETL reached a final answer for the reply: SUCCEEDED, FAILED or UNKNOWN. Nothing else
  // settles a reply — in particular no local timer or read failure.
  const settleReply = (conversationId, reply, status) => {
    const record = pendingReplies.value[conversationId];
    const text = reply?.text ?? record?.text ?? '';
    const mutationId = reply?.mutationId ?? record?.mutationId ?? null;
    const draft = drafts.value[conversationId];

    if (status === 'SUCCEEDED') {
      addOptimistic(conversationId, {
        mutationId,
        messageId: reply?.item?.resultEntityId ?? null,
        text,
        createdDate: reply?.completedAt ?? new Date().toISOString(),
      });
      dropPending(conversationId);
      // Only the draft that was sent: a different text is somebody's next reply.
      if (!draft || draft.text === text) clearDraft(conversationId);
      else saveDraft(conversationId, { text: draft.text, key: null });
      scheduleThreadRefetch(conversationId);
      return;
    }

    if (status === 'FAILED') {
      // Nothing was sent. Its key now names a failed mutation, which the ETL would replay
      // without sending again — so the next try needs a new one.
      setPending(conversationId, {
        status: 'failed',
        text,
        mutationId,
        errorCode: reply?.item?.errorCode || 'EBAY_REJECTED',
        reuseKey: false,
      });
      if (draft) saveDraft(conversationId, { text: draft.text, key: null });
      return;
    }

    // UNKNOWN, as reported by the ETL. The draft keeps its key on purpose: if the seller
    // reloads before confirming, resending the same text replays this very mutation (and shows
    // "unknown" again) instead of sending a second message.
    setPending(conversationId, {
      status: 'unknown',
      text,
      mutationId,
      errorCode: 'OUTCOME_UNKNOWN',
      reuseKey: false,
    });
  };

  // Flags (or unflags) a polled reply as "can't check its status right now". Still in flight.
  const setUnreadable = (conversationId, unreadable) => {
    const record = pendingReplies.value[conversationId];
    if (record?.status !== 'queued' || !!record.unreadable === unreadable) return;
    setPending(conversationId, { ...record, unreadable });
  };

  // Polls GET /replies/{id}: every second for the first 30 s, every 5 s after that, every 30 s
  // once it has been open five minutes. Resolves once the ETL settled the reply (sent / failed /
  // unknown) or the loop was cancelled — it never gives up on its own. A failed read, or a status
  // this build doesn't know, is retried; after POLL_UNREADABLE_AFTER in a row the reply is only
  // flagged `unreadable`, never settled.
  const pollReply = async (conversationId, mutationId) => {
    cancelPoll(conversationId);
    const token = {};
    replyPolls[conversationId] = token;
    const live = () => replyPolls[conversationId] === token;
    const startedAt = Date.now();
    let failures = 0;
    while (live()) {
      const elapsed = Date.now() - startedAt;
      let wait = POLL_SLOW_MS;
      if (elapsed < POLL_FAST_WINDOW_MS) wait = POLL_FAST_MS;
      else if (elapsed >= POLL_BACKOFF_AFTER_MS) wait = POLL_BACKOFF_MS;
      await pause(conversationId, wait);
      if (!live()) return;
      let reply;
      let status;
      try {
        reply = await fetchEbayReply(mutationId);
        status = reply?.item?.status ?? reply?.status;
        if (!OPEN_STATUSES.includes(status) && !SETTLED_STATUSES.includes(status)) {
          throw new Error(`unrecognised reply status ${status}`);
        }
      } catch (e) {
        if (!live()) return;
        noteRepliesOff(errorCodeOf(e));
        failures += 1;
        if (failures >= POLL_UNREADABLE_AFTER) setUnreadable(conversationId, true);
        continue;
      }
      if (!live()) return;
      failures = 0;
      setUnreadable(conversationId, false);
      if (OPEN_STATUSES.includes(status)) continue;
      delete replyPolls[conversationId];
      settleReply(conversationId, reply, status);
      return;
    }
  };

  // Sends the conversation's draft as a reply. The idempotency key is the draft's: minted on
  // the first send and kept for every retry of the same text — a network error or a 5xx on
  // the POST itself leaves it in place, so the retry replays instead of duplicating. It is
  // retired when the text is edited (setDraftText), when the reply fails for good, and on a
  // resend the seller explicitly confirmed after an 'unknown' outcome (`confirmResend`).
  // Without that confirmation an unknown outcome refuses to send anything.
  //
  // A confirmed resend first reads the earlier reply back from the ETL and refuses (no POST)
  // unless the ETL itself still says UNKNOWN or FAILED: a reply that is PENDING / SENDING /
  // RECONCILING is resumed (polled) instead, and one that SUCCEEDED is settled as sent.
  //
  // Resolves `{ ok: true, mutationId, replayed }` or `{ ok: false, errorCode }`; it does not
  // throw, and it returns as soon as the POST is answered — polling carries on in the store.
  // Before an 'unknown' reply may be sent again: ask the ETL what became of it. Resolves null when
  // the ETL still says UNKNOWN (or FAILED) and the resend may go ahead, else the refusal to return.
  const recheckUnknown = async (conversationId, record) => {
    if (!record.mutationId) return { ok: false, errorCode: 'STATUS_UNREADABLE' };
    const epoch = resetToken.value;
    const idle = idleEpoch;
    let reply;
    try {
      reply = await fetchEbayReply(record.mutationId);
    } catch (e) {
      if (epoch !== resetToken.value) return { ok: false, errorCode: 'SUPERSEDED' };
      noteRepliesOff(errorCodeOf(e));
      return { ok: false, errorCode: 'STATUS_UNREADABLE' };
    }
    if (epoch !== resetToken.value || idle !== idleEpoch) return { ok: false, errorCode: 'SUPERSEDED' };
    if (pendingReplies.value[conversationId] !== record) return { ok: false, errorCode: 'IN_FLIGHT' };
    const status = reply?.item?.status ?? reply?.status;
    if (OPEN_STATUSES.includes(status)) {
      // The ETL is still working on the earlier reply: watch it, never send a second one.
      setPending(conversationId, {
        status: 'queued',
        text: record.text,
        mutationId: record.mutationId,
        errorCode: null,
        reuseKey: true,
      });
      pollReply(conversationId, record.mutationId);
      return { ok: false, errorCode: 'IN_FLIGHT' };
    }
    if (status === 'SUCCEEDED') {
      settleReply(conversationId, reply, status);
      return { ok: false, errorCode: 'ALREADY_SENT' };
    }
    if (status === 'UNKNOWN' || status === 'FAILED') return null;
    return { ok: false, errorCode: 'STATUS_UNREADABLE' };
  };

  const sendReply = async (conversationId, { confirmResend = false } = {}) => {
    const accountId = selectedAccountId();
    if (accountId == null || !conversationId) return { ok: false, errorCode: 'NO_ACCOUNT' };
    loadDraft(conversationId);
    const draft = drafts.value[conversationId];
    const text = draft?.text ?? '';
    let current = pendingReplies.value[conversationId];
    if (current && (current.status === 'sending' || current.status === 'queued')) {
      return { ok: false, errorCode: 'IN_FLIGHT' };
    }
    if (current?.status === 'unknown' && !confirmResend) return { ok: false, errorCode: 'CONFIRM_REQUIRED' };
    if (repliesDisabled.value) return { ok: false, errorCode: 'MESSAGE_REPLIES_DISABLED' };
    if (!text.trim()) return { ok: false, errorCode: 'TEXT_EMPTY' };
    if (replyLength(text) > MAX_REPLY_LENGTH) return { ok: false, errorCode: 'TEXT_TOO_LONG' };

    if (confirmResend && current?.status === 'unknown') {
      // One re-check at a time: a second click while the first is reading must not also pass.
      if (rechecking.has(conversationId)) return { ok: false, errorCode: 'IN_FLIGHT' };
      rechecking.add(conversationId);
      let checked;
      try {
        checked = await recheckUnknown(conversationId, current);
        if (!checked) {
          // Still the same unknown record? Then the resend is ours to start, synchronously below.
          current = pendingReplies.value[conversationId];
          if (current?.status !== 'unknown') checked = { ok: false, errorCode: 'IN_FLIGHT' };
        }
      } finally {
        rechecking.delete(conversationId);
      }
      if (checked) return checked;
    }

    // Only an unknown outcome the seller confirmed retires the key; anything else reuses it.
    const resend = confirmResend && current?.status === 'unknown';
    const key = (!resend && draft.key) || newIdempotencyKey();
    saveDraft(conversationId, { text, key }, accountId);
    const epoch = resetToken.value;
    const idle = idleEpoch;
    const sentAt = Date.now();
    setPending(conversationId, { status: 'sending', text, mutationId: null, errorCode: null, reuseKey: true });
    try {
      const answer = await postEbayReply({
        ebayAccountId: accountId,
        conversationId,
        text,
        idempotencyKey: key,
      });
      if (epoch !== resetToken.value) return { ok: false, errorCode: 'SUPERSEDED' };
      // The page went away while the POST was in the air. The ETL has the reply and the draft
      // still holds its key, so reopening the conversation picks it up again (loadReplies).
      if (idle !== idleEpoch) return { ok: true, mutationId: answer.mutationId, replayed: !!answer.replayed };
      setPending(conversationId, {
        status: 'queued',
        text,
        mutationId: answer.mutationId,
        errorCode: null,
        reuseKey: true,
      });
      pollReply(conversationId, answer.mutationId);
      return { ok: true, mutationId: answer.mutationId, replayed: !!answer.replayed };
    } catch (e) {
      if (epoch !== resetToken.value) return { ok: false, errorCode: 'SUPERSEDED' };
      const code = errorCodeOf(e);
      noteRepliesOff(code);
      if (idle !== idleEpoch) return { ok: false, errorCode: code ?? (e?.response ? 'REQUEST_FAILED' : 'NETWORK') };
      let errorCode = code;
      if (!errorCode) errorCode = e?.response ? 'REQUEST_FAILED' : 'NETWORK';
      // A refusal answers before anything is saved and a network error / 5xx may or may not
      // have saved it — either way the same key is right: it replays a saved mutation and
      // starts a new one otherwise. The one exception is a key the ETL already knows with other
      // text, which can only be retried under a new key.
      const reuseKey = code !== 'IDEMPOTENCY_KEY_REUSED';
      // No answer, or a server error: the ETL may have saved the reply before it failed. A
      // refusal (4xx) or "replies are off" (503) answers before anything is saved.
      const status = e?.response?.status;
      const maybeSaved = reuseKey && (!e?.response || (status >= 500 && code !== 'MESSAGE_REPLIES_DISABLED'));
      setPending(conversationId, {
        status: 'failed',
        text,
        mutationId: null,
        errorCode,
        reuseKey,
        ...(maybeSaved ? { maybeSaved: true, sentAt } : {}),
      });
      if (!reuseKey) saveDraft(conversationId, { text, key: null }, accountId);
      return { ok: false, errorCode };
    }
  };

  // Gives up on a reply the ETL ended as FAILED or UNKNOWN and throws the draft away — the seller
  // checked the conversation and does not want to send it. Never touches one still in flight (the
  // ETL would still send it), nor a POST that failed without an answer (it may have been saved).
  const discardReply = (conversationId) => {
    const record = pendingReplies.value[conversationId];
    const terminal = record?.status === 'unknown'
      || (record?.status === 'failed' && !(record.mutationId == null && record.reuseKey));
    if (!terminal) return;
    cancelPoll(conversationId);
    dropPending(conversationId);
    clearDraft(conversationId);
  };

  // Called when a conversation's composer opens. The ETL lists the last 7 days of this
  // conversation's replies, which restores what a reload would otherwise forget (a reply still
  // being sent resumes polling; an unconfirmed one asks for confirmation again; one that settled
  // while nobody was watching is shown as it ended) and tells us whether replies are switched
  // off at all (503), so the composer can say so before anyone types a message. It always
  // re-reads the ETL, even when this session already holds a record for the conversation: a local
  // record may be stale, and an in-flight one whose poll loop is gone must resume. The one thing
  // it never touches is a POST still in the air, or a reply that is already being polled.
  const loadReplies = async (conversationId) => {
    const accountId = selectedAccountId();
    if (accountId == null || !conversationId || repliesDisabled.value) return;
    const epoch = resetToken.value;
    const idle = idleEpoch;
    let answer;
    try {
      answer = await fetchEbayReplies({ ebayAccountId: accountId, conversationId });
    } catch (e) {
      if (epoch === resetToken.value) noteRepliesOff(errorCodeOf(e));
      return;
    }
    if (epoch !== resetToken.value || idle !== idleEpoch) return;
    const record = pendingReplies.value[conversationId];
    if (record?.status === 'sending') return;
    if (record?.status === 'queued' && replyPolls[conversationId]) return;
    const replies = answer?.replies ?? [];
    // With a local record, the ETL's view of THAT mutation; without one, the newest reply.
    let latest = replies[0];
    if (record?.mutationId) {
      latest = replies.find((r) => r.mutationId === record.mutationId);
    } else if (record) {
      // A POST that failed without an answer has no mutation id. If it may have been saved,
      // adopt the ETL's reply carrying that very text, created no earlier than the POST (less
      // some clock skew). Nothing matching: the text stays locked until a same-key retry.
      if (!record.maybeSaved) return;
      latest = replies.find((r) => r.text === record.text
        && Date.parse(r.createdAt) >= record.sentAt - ADOPT_CLOCK_SKEW_MS);
    }
    if (!latest) return;
    const status = latest.item?.status ?? latest.status;
    if (OPEN_STATUSES.includes(status)) {
      setPending(conversationId, {
        status: 'queued',
        text: latest.text ?? '',
        mutationId: latest.mutationId,
        errorCode: null,
        reuseKey: true,
      });
      pollReply(conversationId, latest.mutationId);
    } else if (status === 'UNKNOWN') {
      setPending(conversationId, {
        status: 'unknown',
        text: latest.text ?? '',
        mutationId: latest.mutationId,
        errorCode: 'OUTCOME_UNKNOWN',
        reuseKey: false,
      });
    } else if (status === 'FAILED' || status === 'SUCCEEDED') {
      if (record) {
        // The ETL settled the reply this session was holding: show how it ended.
        settleReply(conversationId, latest, status);
        return;
      }
      // Settled while this browser wasn't watching. It only matters while the composer still
      // holds that very text: a failure is shown next to it, and a success clears the leftover
      // draft (one still carrying the key it was sent under) instead of leaving what looks like
      // an unsent message. A different draft is somebody's next reply and is left alone.
      const draft = drafts.value[conversationId];
      if (!draft || draft.text !== latest.text) return;
      if (status === 'FAILED' || draft.key) settleReply(conversationId, latest, status);
    }
  };

  // The messages page is going away: nobody is watching any reply any more, so every poll loop
  // and refetch timer stops. A reply the ETL is still working on is not lost — the ETL keeps it
  // and the next `loadReplies` (when a composer opens again) picks it up. Failed and unknown
  // notices stay: they still need the seller's decision.
  const stopReplyPolling = () => {
    idleEpoch += 1;
    Object.keys(replyPolls).forEach(cancelPoll);
    refetchTimers.forEach((timer) => clearTimeout(timer));
    refetchTimers.clear();
    const kept = {};
    Object.entries(pendingReplies.value).forEach(([id, record]) => {
      if (record.status !== 'sending' && record.status !== 'queued') kept[id] = record;
    });
    pendingReplies.value = kept;
  };

  const reset = () => {
    filter.value = DEFAULT_FILTER;
    q.value = '';
    sort.value = 'newest';
    marketplaceId.value = '';
    conversationType.value = '';
    listSettled.value = false;
    listing.value = null;
    order.value = null;
    conversations.value = [];
    nextCursor.value = null;
    counts.value = { unanswered: 0, unread: 0 };
    loading.value = false;
    loadingMore.value = false;
    error.value = null;
    selectedId.value = null;
    threads.value = {};
    threadLoading.value = {};
    threadErrors.value = {};
    olderLoading.value = {};
    olderErrors.value = {};
    olderTokens = {};
    summary.value = null;
    summaryError.value = null;
    listToken = null;
    summaryToken = null;
    threadTokens = {};
    Object.keys(replyPolls).forEach(cancelPoll);
    replyPolls = {};
    pauses = {};
    refetchTimers.forEach((timer) => clearTimeout(timer));
    refetchTimers.clear();
    drafts.value = {};
    pendingReplies.value = {};
    optimisticReplies.value = {};
    repliesDisabled.value = false;
    actionBusy.value = {};
    resetToken.value += 1;
  };

  // Keeps the nav badge in sync with this account's summary, including clearing it back to
  // "unknown" (hidden) on reset rather than leaving the previous account's count showing.
  watch(summary, (s) => { unansweredMessagesCount.value = s?.unanswered ?? null; }, { immediate: true });

  // A different eBay account is a different mailbox — drop everything rather than let a
  // stale filter or an open thread from the previous account leak into the new one.
  watch(() => useEbayAccountStore().selectedAccountId, (id, previousId) => {
    if (id === previousId) return;
    // Read BEFORE updating: `hadAccount` must reflect whether we had a real account at any
    // point strictly before this transition, not counting this transition's own `id`. A
    // profile switch goes 7 -> null -> 9: at the null step `hadAccount` is already true (from
    // the initial seed) and stays true (id is null); at the 9 step it reads that same true —
    // sticky across the null in between — so the page still strips the OLD account's
    // c/listing/order instead of replaying them onto 9. `previousId` alone can't do this: on
    // the null -> 9 step `previousId` IS null, which is exactly the false signal the old,
    // non-sticky `previousId != null` check gave.
    resetWasSwitch.value = hadAccount;
    if (id != null) hadAccount = true;
    reset();
  });

  return {
    filter,
    q,
    sort,
    marketplaceId,
    conversationType,
    queryIssue,
    listSettled,
    selectionScope,
    olderLoading,
    olderErrors,
    listing,
    order,
    conversations,
    nextCursor,
    counts,
    loading,
    loadingMore,
    error,
    selectedId,
    threads,
    threadLoading,
    threadErrors,
    summary,
    summaryError,
    drafts,
    pendingReplies,
    optimisticReplies,
    repliesDisabled,
    actionBusy,
    resetToken,
    resetWasSwitch,
    fetchConversations,
    fetchMore,
    setParams,
    fetchThread,
    fetchOlderMessages,
    selectConversation,
    fetchSummary,
    loadDraft,
    setDraftText,
    sendReply,
    pollReply,
    discardReply,
    loadReplies,
    stopReplyPolling,
    markRead,
    refreshThread,
    reset,
  };
});
